import { useEffect, useRef } from "react";

// Section Eyebrow component
export const SectionEyebrow = ({ children, light = false, centered = false }) => (
  <div className={`inline-flex items-center gap-2.5 mb-3 ${centered ? 'justify-center' : ''}`}>
    {!centered && <span className="w-5 h-px bg-[#C9A84C]" />}
    <span className={`text-[11px] font-medium tracking-[0.14em] uppercase ${light ? 'text-[#C9A84C]' : 'text-[#C9A84C]'}`}>
      {children}
    </span>
  </div>
);

// Fade-in section wrapper
export const FadeInSection = ({ children, className = "" }) => {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("visible");
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return (
    <div ref={ref} className={`fade-in-section ${className}`}>
      {children}
    </div>
  );
};

// Proof Strip
export const ProofStrip = () => (
  <div className="bg-[#F0EDE4] border-t border-b border-black/5 py-4 lg:py-5 px-4 lg:px-12" data-testid="proof-strip">
    <div className="flex items-center justify-center gap-2 lg:gap-4 flex-wrap text-xs lg:text-sm text-[#6B7A99]">
      <span className="text-[#C9A84C] tracking-wider">★★★★★</span>
      <span><strong className="text-[#1A2540] font-medium">Trusted by 500+ families</strong> across British Columbia</span>
      <span className="opacity-30 hidden sm:inline">·</span>
      <span className="hidden sm:inline">Independent</span>
      <span className="opacity-30 hidden sm:inline">·</span>
      <span className="hidden sm:inline">Licensed in BC</span>
      <span className="opacity-30 hidden sm:inline">·</span>
      <span className="hidden sm:inline">Advising since 2009</span>
    </div>
  </div>
);

// Partner Strip
export const PartnerStrip = ({ prominent = false }) => (
  <div className={`${prominent ? 'bg-[#FAF8F3] py-10 lg:py-14' : 'bg-white py-6 lg:py-8'} border-b border-black/5 px-4 lg:px-12`} data-testid="partners-strip">
    <div className="max-w-4xl mx-auto text-center">
      {prominent && (
        <>
          <h3 className="font-playfair text-xl lg:text-2xl font-semibold text-[#0C1B33] mb-2">
            Proud partners with Canada's leading benefits providers
          </h3>
          <p className="text-sm text-[#6B7A99] mb-8 max-w-2xl mx-auto">
            We work directly with iA Financial Group, Canada Life, and AGA Benefit Solutions to deliver the most competitive and comprehensive plans available in Canada.
          </p>
        </>
      )}
      {!prominent && (
        <p className="text-[11px] font-medium tracking-[0.12em] text-[#6B7A99] uppercase mb-5">
          Proud partners with Canada's leading insurers
        </p>
      )}
      <div className="flex items-center justify-center gap-8 lg:gap-16 flex-wrap">
        <div className={`flex items-center gap-2 ${prominent ? 'opacity-100' : 'opacity-50 hover:opacity-100'} transition-all duration-300`}>
          <svg viewBox="0 0 40 40" className={`${prominent ? 'w-10 h-10 lg:w-12 lg:h-12' : 'w-8 h-8 lg:w-10 lg:h-10'} text-[#1A2540]`} fill="currentColor">
            <circle cx="20" cy="20" r="16" fill="none" stroke="currentColor" strokeWidth="2"/>
            <circle cx="20" cy="20" r="8" fill="currentColor"/>
          </svg>
          <span className={`font-playfair ${prominent ? 'text-xl lg:text-2xl' : 'text-lg lg:text-xl'} font-semibold text-[#1A2540]`}>iA Financial</span>
        </div>
        <div className="w-px h-8 bg-[#DDD8CC] hidden lg:block" />
        <div className={`flex items-center gap-2 ${prominent ? 'opacity-100' : 'opacity-50 hover:opacity-100'} transition-all duration-300`}>
          <svg viewBox="0 0 40 40" className={`${prominent ? 'w-10 h-10 lg:w-12 lg:h-12' : 'w-8 h-8 lg:w-10 lg:h-10'} text-[#1A2540]`} fill="currentColor">
            <path d="M20 4l-12 8v16l12 8 12-8V12l-12-8zm0 6l6 4v8l-6 4-6-4v-8l6-4z"/>
          </svg>
          <span className={`font-playfair ${prominent ? 'text-xl lg:text-2xl' : 'text-lg lg:text-xl'} font-semibold text-[#1A2540]`}>Canada Life</span>
        </div>
        {prominent && (
          <>
            <div className="w-px h-8 bg-[#DDD8CC] hidden lg:block" />
            <div className="flex items-center gap-2 transition-all duration-300">
              <svg viewBox="0 0 40 40" className="w-10 h-10 lg:w-12 lg:h-12 text-[#1A2540]" fill="currentColor">
                <rect x="8" y="12" width="24" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
                <path d="M14 18h12M14 22h8"/>
              </svg>
              <span className="font-playfair text-xl lg:text-2xl font-semibold text-[#1A2540]">AGA Benefits</span>
            </div>
          </>
        )}
      </div>
    </div>
  </div>
);

// Testimonials Section
export const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "For over 15 years my family and I have trusted Mayker with our life insurance needs. His genuine care for our family — outside of just our insurance — is what sets the service apart.",
      author: "Bryan Reid",
      title: "Kindred Construction"
    },
    {
      quote: "Chris is not only a high integrity professional, he's become a friend who genuinely cares about people and their dreams. If you have a dream of financial independence, call Chris immediately.",
      author: "Sharmila Singh",
      title: "Mayker Client"
    },
    {
      quote: "Chris helped me outline my long-term financial goals and align them with realistic solutions. All I have to do is continue doing what I'm doing, and I know I'll be okay.",
      author: "Patrick Weeks",
      title: "Re/Max Select Properties"
    }
  ];

  return (
    <section className="bg-[#0C1B33] py-16 lg:py-20 px-4 lg:px-12" data-testid="testimonials-section">
      <div className="max-w-6xl mx-auto">
        <FadeInSection>
          <SectionEyebrow light>Client stories</SectionEyebrow>
          <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-white leading-tight mb-10 lg:mb-14">
            BC families who trust Mayker
          </h2>
        </FadeInSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {testimonials.map((testimonial, index) => (
            <FadeInSection key={index}>
              <div className="testimonial-card rounded-lg p-6 lg:p-7 h-full" data-testid={`testimonial-${index}`}>
                <div className="text-[#C9A84C] text-sm tracking-wider mb-3">★★★★★</div>
                <p className="text-[13px] text-white/70 leading-[1.8] mb-5 italic">
                  "{testimonial.quote}"
                </p>
                <div className="text-[13px] font-medium text-[#E8CC80]">{testimonial.author}</div>
                <div className="text-[11px] text-white/30 mt-0.5">{testimonial.title}</div>
              </div>
            </FadeInSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// Final CTA Section
export const FinalCTASection = ({ 
  headline = "Ready to build your financial plan?",
  subheadline = "Book a free 15-minute call — no pressure, no jargon, just honest local advice."
}) => (
  <section className="bg-[#FAF8F3] py-16 lg:py-20 px-4 lg:px-12 text-center" data-testid="final-cta-section">
    <FadeInSection>
      <div className="max-w-xl mx-auto">
        <SectionEyebrow centered>Ready to get started?</SectionEyebrow>
        <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-[#0C1B33] leading-tight mb-4">
          {headline}
        </h2>
        <p className="text-[15px] text-[#6B7A99] leading-relaxed mb-8">
          {subheadline}
        </p>
        <a 
          href="/contact"
          className="inline-block bg-[#0C1B33] text-white text-sm font-medium tracking-wide py-4 px-10 rounded hover:bg-[#162647] hover:-translate-y-0.5 transition-all"
          data-testid="final-cta-button"
        >
          Book a Free Strategy Call →
        </a>
        <div className="my-4 text-xs text-[#6B7A99]">— or call us directly —</div>
        <a 
          href="tel:7788361962"
          className="font-playfair text-xl lg:text-2xl font-semibold text-[#0C1B33] hover:text-[#C9A84C] transition-colors"
          data-testid="final-phone-cta"
        >
          778-836-1962
        </a>
      </div>
    </FadeInSection>
  </section>
);
